import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { servicePost } from "../../api/client.ts";
import { useSession } from "../../hooks/useSession.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import type { ProductCacheItem } from "../../types/session.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Types ──────────────────────────────────────────────── */

export interface AccessProductRelation {
  ID: number;
  ProductID?: number;
  RelationID?: number;
  RelationType?: number;
  Deleted?: boolean;
}

interface RelationGridRow {
  ID: number;
  RelationID: number;
  RelationProduct: string;
  RelationType: number;
  isTemp?: boolean;
}

interface Props {
  productId: number;
  isNew: boolean;
  /** Collect temp relations for new-product save flow. */
  onTempRelationsChange?: (relations: AccessProductRelation[]) => void;
}

const RELATION_TYPES = [
  { value: 8, label: "Recycle Fee" },
];

/* ── Component ──────────────────────────────────────────── */

export function ProductRelationsSection({ productId, isNew, onTempRelationsChange }: Props) {
  const { cache } = useSession();
  const { confirm } = useAlertDialog();
  const [rows, setRows] = useState<RelationGridRow[]>([]);
  const [relationsMap, setRelationsMap] = useState<Record<number, boolean>>({});
  const [tempRelations, setTempRelations] = useState<AccessProductRelation[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [relationType, setRelationType] = useState(8);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  let tempIdCounter = 0;

  // Products from cache for the filterable dropdown
  const allProducts = useMemo(() => {
    return (cache?.CommonData?.Product ?? []) as ProductCacheItem[];
  }, [cache]);

  const filteredProducts = useMemo(() => {
    if (!searchInput.trim()) return allProducts.slice(0, 50);
    const q = searchInput.toLowerCase();
    return allProducts
      .filter(p => p.Name?.toLowerCase().includes(q) || String(p.ProductID).includes(q))
      .slice(0, 50);
  }, [allProducts, searchInput]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Load existing relations for saved products
  useEffect(() => {
    if (isNew || productId <= 0) {
      setRows([]);
      setRelationsMap({});
      setTempRelations([]);
      return;
    }
    setLoading(true);
    servicePost<RelationGridRow[]>("WebGetAccessRecycleRelations", { productId })
      .then(result => {
        if (!result.bError && result.oData) {
          const map: Record<number, boolean> = {};
          const loaded = result.oData.map(r => {
            map[r.RelationID] = true;
            return { ...r, isTemp: false };
          });
          setRows(loaded);
          setRelationsMap(map);
        }
      })
      .finally(() => setLoading(false));
  }, [productId, isNew]);

  // Propagate temp relations to parent for new-product save
  useEffect(() => {
    onTempRelationsChange?.(tempRelations);
  }, [tempRelations, onTempRelationsChange]);

  const handleAdd = useCallback(async () => {
    setMessage(null);
    const relationId = selectedProductId;
    if (!relationId || relationId < 1) {
      setMessage({ type: "error", text: "Select a product for the relation." });
      return;
    }
    if (relationId === productId && !isNew) {
      setMessage({ type: "error", text: "Cannot add a relation to itself." });
      return;
    }
    if (relationsMap[relationId]) {
      setMessage({ type: "error", text: "This relation already exists." });
      return;
    }
    // For Recycle Fee (type 8), only one allowed
    if (relationType === 8) {
      const hasRecycle = rows.some(r => r.RelationType === 8) || tempRelations.some(r => r.RelationType === 8);
      if (hasRecycle) {
        setMessage({ type: "error", text: "Only one Recycle Fee relation is allowed per product." });
        return;
      }
    }

    if (isNew) {
      // Temp relation — saved with the product
      tempIdCounter++;
      const prodName = allProducts.find(p => p.ProductID === relationId)?.Name ?? `#${relationId}`;
      const tempRel: AccessProductRelation = {
        ID: -(tempIdCounter),
        RelationID: relationId,
        RelationType: relationType,
        Deleted: false,
      };
      setTempRelations(prev => [...prev, tempRel]);
      setRows(prev => [...prev, {
        ID: tempRel.ID,
        RelationID: relationId,
        RelationProduct: `(${relationId}) ${prodName}`,
        RelationType: relationType,
        isTemp: true,
      }]);
      setRelationsMap(prev => ({ ...prev, [relationId]: true }));
      setSearchInput("");
      setSelectedProductId(null);
      setMessage({ type: "success", text: "Relation added temporarily. It will be saved with the product." });
    } else {
      // Save immediately
      try {
        const result = await servicePost<boolean>("WebAddAccessRecycleRelationToProduct", {
          relationID: relationId,
          productID: productId,
          relationType,
        });
        if (result.bError) {
          setMessage({ type: "error", text: result.sMessage ?? "Failed to add relation." });
        } else {
          setMessage({ type: "success", text: "Relation added." });
          setSearchInput("");
          setSelectedProductId(null);
          // Reload
          const reload = await servicePost<RelationGridRow[]>("WebGetAccessRecycleRelations", { productId });
          if (!reload.bError && reload.oData) {
            const map: Record<number, boolean> = {};
            const loaded = reload.oData.map(r => { map[r.RelationID] = true; return { ...r, isTemp: false }; });
            setRows(loaded);
            setRelationsMap(map);
          }
        }
      } catch {
        setMessage({ type: "error", text: "Failed to add relation." });
      }
    }
  }, [selectedProductId, relationType, productId, isNew, relationsMap, rows, tempRelations, allProducts]);

  const handleDelete = useCallback(async (row: RelationGridRow) => {
    setMessage(null);
    if (row.isTemp || isNew) {
      // Remove local temp
      setTempRelations(prev => prev.filter(r => r.ID !== row.ID));
      setRows(prev => prev.filter(r => r.ID !== row.ID));
      setRelationsMap(prev => {
        const next = { ...prev };
        delete next[row.RelationID];
        return next;
      });
      setMessage({ type: "success", text: "Temporary relation removed." });
    } else {
      if (!(await confirm({ title: "Delete this product relation?", confirmLabel: "Delete", destructive: true }))) return;
      try {
        const result = await servicePost<boolean>("WebDeleteAccessRecycleRelation", {
          productID: productId,
          relationID: row.RelationID,
        });
        if (result.bError) {
          setMessage({ type: "error", text: result.sMessage ?? "Failed to delete relation." });
        } else {
          setRows(prev => prev.filter(r => r.ID !== row.ID));
          setRelationsMap(prev => {
            const next = { ...prev };
            delete next[row.RelationID];
            return next;
          });
          setMessage({ type: "success", text: "Relation deleted." });
        }
      } catch {
        setMessage({ type: "error", text: "Failed to delete relation." });
      }
    }
  }, [productId, isNew]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <p className={styles.hint}>
        Search for a product by name, choose a relation type, then click Add Product Relation. Remove existing relations using the Delete button.
      </p>

      {/* Search + type + add */}
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className={styles.fieldRowStacked} style={{ flex: 1, minWidth: 200, maxWidth: 400, position: "relative" }} ref={wrapperRef}>
          <label>Search for product</label>
          <input
            className={styles.fieldInput}
            value={searchInput}
            onChange={e => {
              setSearchInput(e.target.value);
              setSelectedProductId(null);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            placeholder="Free text search"
            autoComplete="off"
          />
          {dropdownOpen && filteredProducts.length > 0 && (
            <ul style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
              maxHeight: 240, overflowY: "auto",
              background: "var(--color-bg)", border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-1)", boxShadow: "var(--shadow-md)",
              listStyle: "none", margin: 0, padding: 0,
            }}>
              {filteredProducts.map(p => (
                <li
                  key={p.ProductID}
                  style={{
                    padding: "var(--space-1) var(--space-2)", cursor: "pointer",
                    fontSize: "var(--text-sm)",
                    background: selectedProductId === p.ProductID ? "var(--color-bg-muted)" : undefined,
                  }}
                  onMouseDown={e => {
                    e.preventDefault();
                    setSelectedProductId(p.ProductID);
                    setSearchInput(`(${p.ProductID}) ${p.Name}`);
                    setDropdownOpen(false);
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--color-bg-sidebar)")}
                  onMouseLeave={e => (e.currentTarget.style.background = selectedProductId === p.ProductID ? "var(--color-bg-muted)" : "")}
                >
                  ({p.ProductID}) {p.Name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={styles.fieldRowStacked} style={{ minWidth: 150 }}>
          <label>Relation Type</label>
          <select className={styles.fieldSelect} value={relationType} onChange={e => setRelationType(parseInt(e.target.value))}>
            {RELATION_TYPES.map(rt => (
              <option key={rt.value} value={rt.value}>{rt.label}</option>
            ))}
          </select>
        </div>
        <button className={styles.secondaryBtn} onClick={handleAdd} style={{ marginBottom: 2 }}>
          Add Product Relation
        </button>
      </div>

      {message && (
        <div className={`${styles.statusMsg} ${message.type === "success" ? styles.statusSuccess : styles.statusError}`}>
          {message.text}
        </div>
      )}

      {/* Relations grid */}
      {loading ? (
        <p>Loading relations...</p>
      ) : rows.length === 0 ? (
        <p className={styles.hint}>No product relations.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--color-border-default)", textAlign: "left" }}>
              <th style={{ padding: "var(--space-1) var(--space-2)" }}>Relation ID</th>
              <th style={{ padding: "var(--space-1) var(--space-2)" }}>Relation Product</th>
              <th style={{ padding: "var(--space-1) var(--space-2)" }}>Type</th>
              <th style={{ padding: "var(--space-1) var(--space-2)", width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.ID} style={{ borderBottom: "1px solid var(--color-border-default)" }}>
                <td style={{ padding: "var(--space-1) var(--space-2)" }}>{row.RelationID}</td>
                <td style={{ padding: "var(--space-1) var(--space-2)" }}>{row.RelationProduct}</td>
                <td style={{ padding: "var(--space-1) var(--space-2)" }}>
                  {RELATION_TYPES.find(rt => rt.value === row.RelationType)?.label ?? row.RelationType}
                </td>
                <td style={{ padding: "var(--space-1) var(--space-2)", textAlign: "center" }}>
                  <button
                    className={styles.secondaryBtn}
                    onClick={() => handleDelete(row)}
                    style={{ padding: "2px 8px", fontSize: "var(--text-xs)", color: "var(--color-error)" }}
                    title="Delete relation"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
